import { useEffect, useMemo, useRef, useState } from "react";
import { Application, ReviewStatus } from "@/types/application";
import {
  InterpreterEntities,
  useInterpreterStore,
} from "@coltorapps/builder-react";
import { entitiesComponents } from "@/const/form-builder";
import { basicFormBuilder } from "./form/builder/definition";
import { usePreloadMasterDataForExpressions } from "@/hooks/useMasterData";
import { useCodeHelper } from "@/hooks/useCode/useCodeHelper";
import { useReactiveDefaultValues } from "@/hooks/useReactiveDefaultValues";
import { flushFormCommits } from "@/utils/form-commit-registry";
import { useSetAtom } from "jotai";
import {
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import { EntitiesValues } from "@coltorapps/builder";
import ApproveRejectTab, {
  useApproveRejectTab,
} from "./tabs/reject-approve-tab";
import { Textarea } from "@ui/textarea";
import { Button } from "@ui/button";
import { Loader2 } from "lucide-react";
import {
  useApproveApplication,
  useRejectApplication,
} from "@/hooks/useApplication";
import { useToast } from "@ui/toast";
import { ZodError } from "zod";
import { useTranslation } from "react-i18next";
import useValidatorStore from "@/hooks/useValidatorStore";
import { parseFormData } from "@/utils/parser";
import ApproverReadonlyForm from "./ApproverReadonlyForm";
import { getApiErrorMessage } from "@/utils/api-error";

type ApproverFormDraft = {
  values: EntitiesValues<typeof basicFormBuilder>;
};

const approverFormDrafts = new Map<string, ApproverFormDraft>();

type Props = {
  application: Application;
  onChange?: (values?: EntitiesValues<typeof basicFormBuilder>) => void;
  onBusyChange?: (isBusy: boolean) => void;
};

export default function ApproverForm({ application, onBusyChange }: Props) {
  if (
    application.reviewStatus === ReviewStatus.Approved ||
    application.reviewStatus === ReviewStatus.Rejected
  ) {
    return <ApproverReadonlyForm application={application} />;
  }

  const { formInstance } = application;
  const { form, data } = formInstance;
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { decision, setDecision } = useApproveRejectTab();
  const [comment, setComment] = useState("");
  const setIStore = useSetAtom(interpreterStoreAtom);
  const setRuntimeApplication = useSetAtom(runtimeApplicationAtom);
  const setSelectedApplicant = useSetAtom(selectedApplicantAtom);
  const appliedIncomingDataKeyRef = useRef<string | null>(null);
  const applicationIdentity = `${application.serialNumber}:${application.approvalId}`;
  const schemaSignature = useMemo(
    () => JSON.stringify(form.schema ?? {}),
    [form.schema],
  );
  const draftCacheKey = `${applicationIdentity}:${schemaSignature}`;
  const stableFormSchema = useMemo(() => form.schema, [schemaSignature]);

  const initialDataSignature = useMemo(
    () => JSON.stringify(data ?? {}),
    [data],
  );
  const stableInitialData = useMemo(() => data ?? {}, [initialDataSignature]);

  const { getCompiledSchema } = useCodeHelper({
    formSchema: stableFormSchema,
    formData: stableInitialData,
    application,
  });
  const {
    tableKeys: expressionMasterDataTableKeys,
    cache: expressionMasterDataCache,
  } = usePreloadMasterDataForExpressions(stableFormSchema);
  const isExpressionMasterDataLoading = expressionMasterDataTableKeys.some(
    (tableKey) => {
      const status = expressionMasterDataCache[tableKey]?.status;
      return !status || status === "idle" || status === "loading";
    },
  );

  const rawCompiledSchema = useMemo(
    () => getCompiledSchema(),
    [getCompiledSchema, expressionMasterDataCache],
  );
  const compiledSchemaSignature = useMemo(
    () => JSON.stringify(rawCompiledSchema ?? {}),
    [rawCompiledSchema],
  );
  const compiledSchema = useMemo(
    () => rawCompiledSchema,
    [compiledSchemaSignature],
  );
  const latestEntitiesValues = approverFormDrafts.get(draftCacheKey)?.values;
  const effectiveInitialData = useMemo(
    () => ({
      ...stableInitialData,
      ...(latestEntitiesValues ?? {}),
    }),
    [latestEntitiesValues, stableInitialData],
  );

  const interpreterStore = useInterpreterStore(
    basicFormBuilder,
    compiledSchema,
    {
      initialData: {
        entitiesValues: effectiveInitialData,
        entitiesErrors: {},
      },
    },
  );
  const appliedIncomingStoreRef = useRef<typeof interpreterStore | null>(null);

  useReactiveDefaultValues(interpreterStore, stableFormSchema);
  const { toast } = useToast();
  function handleApiError(error: Error) {
    toast({
      variant: "destructive",
      title: t("toast.validation_failed"),
      description: getApiErrorMessage(error),
    });
  }

  const { mutate: approveApplication, isPending: isApproving } =
    useApproveApplication({
      onSuccess: () => {
        setIsSubmitted(true);
        approverFormDrafts.delete(draftCacheKey);
        toast({
          variant: "success",
          title: "Successfully approve application.",
        });
        // navigate("/dashboard?tab=approval");
      },
      onError: handleApiError,
    });
  const { mutate: rejectApplication, isPending: isRejecting } =
    useRejectApplication({
      onSuccess: () => {
        setIsSubmitted(true);
        approverFormDrafts.delete(draftCacheKey);
        toast({
          variant: "success",
          title: "Successfully reject application.",
        });
        // navigate("/dashboard?tab=approval");
      },
      onError: handleApiError,
    });
  const isSubmitting = isApproving || isRejecting || isValidating;

  useEffect(() => {
    onBusyChange?.(isSubmitting);
  }, [isSubmitting, onBusyChange]);

  useEffect(() => {
    return () => {
      onBusyChange?.(false);
    };
  }, [onBusyChange]);

  const {
    executeAllRegistry,
    executeAllLocalValidator,
    executeFormValidator,
    initiateValidatorStore,
  } = useValidatorStore();

  function handleReviewSubmit() {
    void handleValidation({
      blockOnError: decision === "approve",
      showErrorToast: decision === "approve",
    });
  }

  async function handleValidation({
    blockOnError,
    showErrorToast,
  }: {
    blockOnError: boolean;
    showErrorToast: boolean;
  }) {
    if (isValidating || isApproving || isRejecting) return;
    if (!decision) return;

    setIsValidating(true);

    const isValid = await runValidation({ showErrorToast });

    if (!isValid && blockOnError) {
      setIsValidating(false);
      return;
    }

    handleSubmit();
    setIsValidating(false);
  }

  async function runValidation({
    showErrorToast,
  }: {
    showErrorToast: boolean;
  }) {
    try {
      await flushFormCommits();
    } catch (error) {
      if (showErrorToast) {
        toast({
          variant: "destructive",
          title: t("toast.validation_failed"),
          description:
            error instanceof Error
              ? error.message
              : t("toast.fill_out_form_correctly"),
        });
      }
      return false;
    }

    try {
      const result = await interpreterStore.validateEntitiesValues();
      const data = interpreterStore.getData();

      const registryErrors = await executeAllRegistry(
        data.entitiesValues,
        interpreterStore.schema,
      );
      const localErrors = await executeAllLocalValidator(data.entitiesValues);
      const formErrors = await executeFormValidator();

      const mergedErrors: Record<string, string> = {
        ...localErrors,
        ...registryErrors,
      };
      const hasEntityErrors = Object.keys(mergedErrors).length > 0;
      const hasFormErrors = Object.keys(formErrors).length > 0;

      if (hasEntityErrors) {
        const entitiesErrors = Object.entries(mergedErrors).reduce(
          (acc, [entityId, message]) => {
            acc[entityId] = new ZodError([
              { code: "custom", message, path: [] },
            ]);
            return acc;
          },
          {} as Record<string, ZodError>,
        );
        interpreterStore.setEntitiesErrors(entitiesErrors);
      }

      if (hasFormErrors && showErrorToast) {
        const formErrorMessage = Object.values(formErrors)[0];
        toast({
          variant: "destructive",
          title: t("toast.validation_failed"),
          description: formErrorMessage ?? t("toast.fill_out_form_correctly"),
        });
      }

      if (result.success && !hasEntityErrors && !hasFormErrors) {
        return true;
      }

      if (
        !result.success &&
        !hasEntityErrors &&
        !hasFormErrors &&
        showErrorToast
      ) {
        console.warn(result);
        toast({
          variant: "destructive",
          title: t("toast.validation_failed"),
          description: t("toast.fill_out_form_correctly"),
        });
      }

      return false;
    } finally {
      setIsValidating(false);
    }
  }

  function handleSubmit() {
    const entitiesValues = interpreterStore.getEntitiesValues();
    const { data: mappedData } = parseFormData(
      entitiesValues,
      formInstance.form.schema,
    );
    if (decision === "approve") {
      approveApplication({
        serialNumber: application.serialNumber,
        comment,
        approvalId: application.approvalId,
        formData: mappedData,
      });
    } else if (decision === "reject") {
      rejectApplication({
        serialNumber: application.serialNumber,
        comment,
        approvalId: application.approvalId,
        formData: mappedData,
      });
    }
  }

  useEffect(() => {
    const hasAppliedCurrentApplicationData =
      appliedIncomingDataKeyRef.current === draftCacheKey &&
      appliedIncomingStoreRef.current === interpreterStore;

    if (hasAppliedCurrentApplicationData) {
      approverFormDrafts.set(draftCacheKey, {
        values: interpreterStore.getEntitiesValues(),
      });
      return;
    }

    const nextEntitiesValues = {
      ...interpreterStore.getEntitiesValues(),
      ...data,
      ...(approverFormDrafts.get(draftCacheKey)?.values ?? {}),
    };
    interpreterStore.setData({
      entitiesValues: nextEntitiesValues,
      entitiesErrors: {},
    });
    approverFormDrafts.set(draftCacheKey, {
      values: nextEntitiesValues,
    });
    appliedIncomingDataKeyRef.current = draftCacheKey;
    appliedIncomingStoreRef.current = interpreterStore;
  }, [applicationIdentity, data, draftCacheKey, interpreterStore]);

  useEffect(() => {
    approverFormDrafts.set(draftCacheKey, {
      values: interpreterStore.getEntitiesValues(),
    });

    const unsubscribe = interpreterStore.subscribe(() => {
      approverFormDrafts.set(draftCacheKey, {
        values: interpreterStore.getEntitiesValues(),
      });
    });

    return unsubscribe;
  }, [
    application.approvalId,
    application.serialNumber,
    draftCacheKey,
    interpreterStore,
  ]);

  useEffect(() => {
    initiateValidatorStore(compiledSchema);
  }, [compiledSchema]);

  useEffect(() => {
    setIStore((prev) => (prev === interpreterStore ? prev : interpreterStore));
  }, [interpreterStore, setIStore]);

  useEffect(() => {
    setRuntimeApplication(application);
  }, [application, setRuntimeApplication]);

  useEffect(() => {
    // Keep expression context aligned with ApplicationForm behavior.
    setSelectedApplicant(application.applicant ?? null);
  }, [application.applicant, setSelectedApplicant]);

  if (isExpressionMasterDataLoading) {
    return (
      <div className="flex items-center justify-center rounded-md bg-white/70 py-8 text-sm text-gray-600">
        {t("loading")}
      </div>
    );
  }

  const isBusy = isSubmitting || isSubmitted;

  return (
    <div className="relative">
      {isBusy && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-md bg-white/70">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          <span className="text-sm text-gray-600">{t("loading")}</span>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // handleSubmit();
        }}
        onKeyDownCapture={(e) => {
          if (e.key !== "Enter") return;
          const target = e.target as HTMLElement | null;
          if (target?.tagName.toLowerCase() === "textarea") return;
          e.preventDefault();
        }}
        className="space-y-6"
      >
        <InterpreterEntities
          interpreterStore={interpreterStore}
          components={entitiesComponents}
        />

        <div className="flex flex-col py-4 gap-3">
          {application.reviewStatus === ReviewStatus.Pending && (
            <div className="flex flex-col gap-3">
              <div className="text-sm font-medium text-dark/50">Decisions</div>
              <ApproveRejectTab decision={decision} setDecision={setDecision} />
            </div>
          )}
          <div className="flex flex-col gap-3">
            <dt className="text-sm font-medium text-dark">Comment</dt>
            <dd className="mt-1 text-base text-dark font-semibold whitespace-pre-wrap flex flex-row gap-3">
              <Textarea
                className="h-29"
                value={application.comment ?? comment}
                disabled={!!application.comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </dd>
          </div>
        </div>
        {application.reviewStatus === ReviewStatus.Pending && (
          <div className="flex flex-col items-center pt-5">
            <Button
              // variant={"destructive"}
              type="button"
              onClick={handleReviewSubmit}
              loading={isBusy}
              disabled={!decision}
            >
              Submit
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
